// dashboard-agent-ia/src/components/PhoneInput.js
import React, { useState, useRef, useEffect, useCallback } from 'react'
import axios from 'axios'

const API_URL = process.env.REACT_APP_API_URL || 'https://agent-ia-senegal.onrender.com'

export const availableCountries = [
  { code: 'SN', name: 'Sénégal', dial: '+221', flag: 'sn', digits: 9, mask: '## ### ## ##' },
  { code: 'ML', name: 'Mali', dial: '+223', flag: 'ml', digits: 8, mask: '## ## ## ##' },
  { code: 'CI', name: "Côte d'Ivoire", dial: '+225', flag: 'ci', digits: 10, mask: '## ## ## ## ##' },
]
export { availableCountries as countries }

const applyMask = (digits, mask) => {
  let result = ''
  let idx = 0
  for (let i = 0; i < mask.length && idx < digits.length; i++) {
    if (mask[i] === '#') result += digits[idx++]
    else if (idx < digits.length) result += mask[i]
  }
  return result
}

export const cleanDigits = (val) => val.replace(/\D/g, '')
export const formatPhoneDB = (digits, dial) => `${dial}${digits}`
export const formatPhoneDisplay = (digits, country) => {
  if (!digits || digits.length < 2) return ''
  return `(${country.dial}) ${applyMask(digits, country.mask)}`
}

const PhoneInput = ({ value, onChange, selectedCountry, setSelectedCountry, onCountryChange, error, phonePreviewLabel, checkExists = false, onExistsChange }) => {
  const [open, setOpen] = useState(false)
  const [existsError, setExistsError] = useState('')
  const [checking, setChecking] = useState(false)
  const dropdownRef = useRef(null)
  const checkTimer = useRef(null)

  useEffect(() => {
    const fn = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', fn)
    return () => document.removeEventListener('mousedown', fn)
  }, [])

  const digits = cleanDigits(value)
  const masked = applyMask(digits, selectedCountry.mask)
  const isValid = digits.length === selectedCountry.digits
  const isTyping = digits.length > 0 && digits.length < selectedCountry.digits

  const checkPhone = useCallback(async (phone) => {
    if (!checkExists) return
    setChecking(true)
    try {
      const res = await axios.get(`${API_URL}/api/auth/check-phone?phone=${encodeURIComponent(phone)}`)
      if (res.data.exists) { setExistsError('Ce numéro est déjà associé à un compte.'); onExistsChange?.(true) }
      else { setExistsError(''); onExistsChange?.(false) }
    } catch { setExistsError(''); onExistsChange?.(false) }
    finally { setChecking(false) }
  }, [checkExists, onExistsChange])

  useEffect(() => {
    if (isValid && checkExists) {
      clearTimeout(checkTimer.current)
      checkTimer.current = setTimeout(() => checkPhone(formatPhoneDB(digits, selectedCountry.dial)), 600)
    } else { setExistsError(''); onExistsChange?.(false) }
    return () => clearTimeout(checkTimer.current)
  }, [digits, isValid, checkExists, checkPhone, selectedCountry, onExistsChange])

  const handleInputChange = (e) => {
    const newDigits = cleanDigits(e.target.value)
    if (newDigits.length > selectedCountry.digits) return
    onChange({ target: { value: newDigits } })
  }

  const displayError = error || existsError

  return (
    <div>
      <div className="flex gap-2 items-stretch">
        <div className="relative flex-shrink-0" ref={dropdownRef}>
          <button type="button" onClick={() => setOpen(!open)}
            className="flex items-center gap-2 px-3 border-2 border-blue-500 rounded-xl hover:border-blue-600 focus:outline-none bg-white transition-all"
            style={{height:'52px', minWidth:'110px'}}>
            <img src={`https://flagcdn.com/${selectedCountry.flag}.svg`} alt={selectedCountry.code}
              style={{width:'24px',height:'16px',objectFit:'cover',borderRadius:'3px'}} />
            <span className="text-gray-700 font-semibold text-sm">{selectedCountry.dial}</span>
            <svg className={`w-3 h-3 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {open && (
            <div className="absolute top-full left-0 mt-1 w-60 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden">
              {availableCountries.map(c => (
                <button key={c.code} type="button"
                  onClick={() => { setSelectedCountry(c); onCountryChange?.(c); setOpen(false); setExistsError(''); onExistsChange?.(false); onChange({ target: { value: '' } }) }}
                  className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-blue-50 transition-colors text-left ${selectedCountry.code === c.code ? 'bg-blue-50' : ''}`}>
                  <img src={`https://flagcdn.com/${c.flag}.svg`} alt={c.code} style={{width:'28px',height:'18px',objectFit:'cover',borderRadius:'3px'}} />
                  <div><p className="text-sm font-semibold text-gray-800">{c.name}</p><p className="text-xs text-gray-400">{c.dial} · {c.digits} chiffres</p></div>
                  {selectedCountry.code === c.code && <span className="ml-auto text-blue-500 font-bold">✓</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex-1 relative">
          <input type="tel" value={masked} onChange={handleInputChange}
            style={{height:'52px'}}
            className={`w-full px-4 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-300 text-gray-800 font-mono tracking-wide transition-all
              ${displayError ? 'border-red-400 bg-red-50' : isValid && !existsError ? 'border-green-500' : 'border-blue-500'}`}
            placeholder={selectedCountry.mask.replace(/#/g, '_')} />
          {checking && <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"/>}
          {isValid && !displayError && !checking && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500 font-bold">✓</span>}
          {existsError && !checking && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-red-500 font-bold">✗</span>}
        </div>
      </div>

      {displayError && <p className="text-red-500 text-xs mt-1.5">⚠️ {displayError}</p>}
      {isValid && !displayError && !checking && (
        <p className="text-green-600 text-xs mt-1.5">✅ {phonePreviewLabel} : <strong className="font-mono">{formatPhoneDisplay(digits, selectedCountry)}</strong></p>
      )}
      {isTyping && !displayError && (
        <div className="mt-1.5 flex items-center gap-2">
          <div className="flex gap-0.5">
            {Array.from({ length: selectedCountry.digits }).map((_, i) => (
              <div key={i} className={`h-1 w-3 rounded-full ${i < digits.length ? 'bg-blue-500' : 'bg-gray-200'}`} />
            ))}
          </div>
          <span className="text-gray-400 text-xs">{digits.length}/{selectedCountry.digits}</span>
        </div>
      )}
    </div>
  )
}

export default PhoneInput
